import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { filesize } from 'filesize';
import { Plus } from 'lucide-react';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useTenantStore } from '@/hooks/data/tenant/useTenantStore';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useCapabilities } from '@/hooks/useCapabilities';
import { type InsightKey } from '@/types/tenantInsights';
import { EmptyLayout } from '@/components/empty-layout';
import { Favorites } from '@/components/home/favorites';
import { Insight } from '@/components/home/insight';
import { useTree } from '@/hooks/data/tree/useTree';
import { useHome } from '@/hooks/home/useHome';
import { DAMNodeType } from '@/types/tree';
import { formatCount } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import Image from 'next/image';

export const HomeLayoutComponent = ({ isPending }: { isPending: boolean }) => {
  const openModal = useOpenModal();
  const tEmptyStates = useTranslations('emptyStates');
  const tCommon = useTranslations('common');
  const tHome = useTranslations('home');

  const {
    profile: {
      user: { name: userName },
    },
  } = useAccount();
  const { tree } = useTree();
  const { homeData } = useHome();
  const { tenant } = useTenantStore(useShallow((s) => ({ tenant: s.tenantData?.tenant })));
  const { resolvedTheme } = useTheme();
  const { temporaryGuestBlockForHomePage } = useCapabilities();

  const formatInsight = (label: string, value: number) => {
    const insightsMapping: Record<
      InsightKey,
      {
        label: string;
        formatter: (value: number) => string;
      }
    > = {
      asset_count: { label: 'Assets', formatter: formatCount },
      shared_asset_count: { label: 'Shared files', formatter: formatCount },
      total_storage_bytes_used: {
        label: 'Storage',
        formatter: (val) =>
          filesize(val, {
            round: 2,
            base: 2,
            standard: 'jedec',
          }),
      },
    };

    const mapping = insightsMapping[label as InsightKey];
    if (!mapping) {
      return null;
    }

    return {
      label: mapping.label,
      value: mapping.formatter(value),
    };
  };

  const insights = useMemo(() => {
    if (!homeData?.insights) {
      return [];
    }

    return Object.entries(homeData.insights)
      .map(([label, value]) => formatInsight(label, value as number))
      .filter((item): item is { label: string; value: string } => item !== null);
  }, [homeData]);

  const favorites = useMemo(() => {
    if (!homeData?.favorites) {
      return [];
    }

    return [...homeData.favorites].sort((a, b) => {
      const typeOrder = {
        [DAMNodeType.Folder]: 0,
        [DAMNodeType.Album]: 1,
        [DAMNodeType.Asset]: 2,
      };
      const typeDiff = (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99);
      if (typeDiff !== 0) {
        return typeDiff;
      }
      return a.node.name.localeCompare(b.node.name, undefined, { sensitivity: 'base' });
    });
  }, [homeData]);

  return (
    <div className="flex h-full flex-col gap-y-8 overflow-auto bg-ui-panel p-12 focus-visible:outline-none">
      <div className="flex w-full flex-col gap-y-3">
        <div className="relative max-h-32 min-h-32 w-full">
          <Image
            fill
            alt="home-logo"
            src={
              process.env.NEXT_PUBLIC_ENVIRONMENT === 'local'
                ? `/lkbks-${resolvedTheme}.svg`
                : (tenant?.logo ?? `${process.env.NEXT_PUBLIC_ASSET_PREFIX}/lkbks-${resolvedTheme}.svg`)
            }
            style={{ objectFit: 'contain', objectPosition: 'left center' }}
            draggable={false}
          />
        </div>
        <div className="inline text-6xl leading-normal font-thin">
          {tHome.rich('welcome', {
            name: userName,
            userName: (chunks) => <span className="inline-block max-w-full truncate align-bottom">{chunks}</span>,
          })}
        </div>

        <div className="text-base font-normal">{tHome('intro')}</div>
      </div>
      {insights && (
        <div
          className="grid grid-cols-[repeat(var(--insights-cols),minmax(0,1fr))] gap-4"
          style={{ '--insights-cols': String(Math.min(4, insights.length)) } as React.CSSProperties}
        >
          {insights.map(({ label, value }: { label: string; value: string }) => (
            <Insight key={label} label={label} value={value} />
          ))}
        </div>
      )}
      {favorites.length > 0 && (
        <div className="flex flex-col gap-y-3">
          <div className="text-xl font-bold">{tCommon('favorites')}</div>
          <Favorites favorites={favorites} />
        </div>
      )}
      {!temporaryGuestBlockForHomePage && !isPending && tree.length === 0 ? (
        <EmptyLayout
          title={tEmptyStates('noFolders.title')}
          message={tEmptyStates('noFolders.description')}
          onClick={() => openModal(ModalId.CreateFolder)}
          buttonIcon={Plus}
          buttonText={tEmptyStates('noFolders.buttonLabel')}
          containerClassName="h-auto items-start bg-ui-panel"
          messageClassName="px-0 text-start"
        />
      ) : null}
    </div>
  );
};
